// Card height states (px) — desktop scroll sequence.
const CARD_HEIGHT = {
  EXPANDED: 464,
  PARTIAL: 304,
  COLLAPSED: 156,
};

const IMAGE_SCALE = {
  ACTIVE: 1,
  INACTIVE: 1.15,
};

const DETAILS_OPACITY = {
  EXPANDED: 1,
  COLLAPSED: 0,
};

// The pinned block shrinks as the cards collapse, but ScrollTrigger reserves
// the taller initial height in its pin-spacer — leaving blank space below the
// section. A negative bottom margin on the pinned block is copied onto the
// spacer by ScrollTrigger, pulling the following content up to close that gap.
// Increase to remove more bottom space, decrease to leave more. Tweak to taste.
const PIN_BOTTOM_SPACE_TRIM = 148;

// Agency Partners scroll behaviour. Desktop only (>= 1199px), matching the
// project breakpoint; gsap.matchMedia handles enable/disable on resize.

class AgencyPartnersScroll {
  constructor() {
    this.section = document.querySelector(".section--agency-partners");
    if (!this.section) return;

    this.scrollBlock = this.section.querySelector(".agency-partners__scroll");
    this.cardsContainer = this.scrollBlock?.querySelector(
      ".agency-partners__cards",
    );
    if (!this.scrollBlock || !this.cardsContainer) return;

    this.cardItems = Array.from(
      this.cardsContainer.querySelectorAll(".agency-partner-card"),
    );
    if (!this.cardItems.length) return;

    // Viewport-relative distance per card transition (used for pin end).
    this.getScrollPerCard = () => window.innerHeight * 0.75;

    this.init();
  }

  getPinScrollDistance() {
    const cardCount = this.cardItems.length;
    if (cardCount <= 1) return 0;

    return (cardCount - 1) * this.getScrollPerCard();
  }

  init() {
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", () => this.setup())
      : this.setup();
  }

  setup() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    this.mm = gsap.matchMedia();
    this.mm.add("(min-width: 1199px)", () => {
      this.setInitialCardHeights();
      this.setInitialImageScales();
      this.setInitialDetailsOpacity();
      // Must be set before the pin is created so ScrollTrigger copies it onto
      // the pin-spacer that reserves the section's bottom space.
      gsap.set(this.scrollBlock, { marginBottom: -PIN_BOTTOM_SPACE_TRIM });
      this.expandTimeline = this.createExpandTimeline();

      return () => {
        if (this.expandTimeline) {
          if (this.expandTimeline.scrollTrigger) {
            this.expandTimeline.scrollTrigger.kill();
          }
          this.expandTimeline.kill();
        }
        gsap.set(this.scrollBlock, { clearProps: "marginBottom" });
        gsap.set(this.cardItems, { clearProps: "height,overflow" });
        gsap.set(this.getCardImages(), { clearProps: "transform" });
        gsap.set(this.getAllCardDetailsElements(), { clearProps: "opacity" });
      };
    });

    // Other ScrollTriggers may have run first; recalc start/end after setup.
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  getCardImage(card) {
    return card.querySelector("figure img");
  }

  getCardImages() {
    return this.cardItems
      .map((card) => this.getCardImage(card))
      .filter(Boolean);
  }

  getCardDetailsElement(card) {
    return card.querySelector(".agency-partner-card__details");
  }

  getAllCardDetailsElements() {
    return this.cardItems
      .map((card) => this.getCardDetailsElement(card))
      .filter(Boolean);
  }

  setInitialCardHeights() {
    this.cardItems.forEach((card, index) => {
      let height = CARD_HEIGHT.COLLAPSED;

      if (index === 0) {
        height = CARD_HEIGHT.EXPANDED;
      } else if (index === 1) {
        height = CARD_HEIGHT.PARTIAL;
      }

      gsap.set(card, { height, overflow: "hidden" });
    });
  }

  setInitialImageScales() {
    this.cardItems.forEach((card, index) => {
      const image = this.getCardImage(card);
      if (!image) return;

      gsap.set(image, {
        scale: index === 0 ? IMAGE_SCALE.ACTIVE : IMAGE_SCALE.INACTIVE,
        transformOrigin: "center center",
        force3D: true,
      });
    });
  }

  setInitialDetailsOpacity() {
    this.cardItems.forEach((card, index) => {
      const details = this.getCardDetailsElement(card);
      if (!details) return;

      gsap.set(details, {
        opacity:
          index === 0 ? DETAILS_OPACITY.EXPANDED : DETAILS_OPACITY.COLLAPSED,
      });
    });
  }

  createExpandTimeline() {
    if (this.getPinScrollDistance() <= 0) return null;

    const timeline = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: this.scrollBlock,
        start: "top top",
        end: () => `+=${this.getPinScrollDistance()}`,
        pin: this.scrollBlock,
        pinSpacing: true,
        scrub: true,
        markers: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    for (let i = 0; i < this.cardItems.length - 1; i += 1) {
      const currentCard = this.cardItems[i];
      const nextCard = this.cardItems[i + 1];
      const currentImage = this.getCardImage(currentCard);
      const nextImage = this.getCardImage(nextCard);
      const currentDetails = this.getCardDetailsElement(currentCard);
      const nextDetails = this.getCardDetailsElement(nextCard);
      const stepLabel = `step-${i}`;

      timeline.to(currentCard, { height: CARD_HEIGHT.COLLAPSED }, stepLabel);
      timeline.to(nextCard, { height: CARD_HEIGHT.EXPANDED }, stepLabel);

      if (currentImage) {
        timeline.to(
          currentImage,
          { scale: IMAGE_SCALE.INACTIVE, force3D: true },
          stepLabel,
        );
      }

      if (nextImage) {
        timeline.to(
          nextImage,
          { scale: IMAGE_SCALE.ACTIVE, force3D: true },
          stepLabel,
        );
      }

      if (currentDetails) {
        timeline.to(
          currentDetails,
          { opacity: DETAILS_OPACITY.COLLAPSED },
          stepLabel,
        );
      }

      if (nextDetails) {
        timeline.to(
          nextDetails,
          { opacity: DETAILS_OPACITY.EXPANDED },
          stepLabel,
        );
      }
    }

    return timeline;
  }
}

if (document.querySelector(".agency-partners__cards")) {
  new AgencyPartnersScroll();
}
